import React from "react";
import { Check, Star, Zap } from "lucide-react";

export default function PricingCard({ plan, billingPeriod = "monthly", onSelect }) {
  const isNativeYearly = plan.price_period === "year";
  const showYearly = billingPeriod === "yearly" && !isNativeYearly;

  const displayPrice = showYearly ? Math.round(plan.price * 0.8) : plan.price;
  const annualSaving = showYearly ? Math.round(plan.price * 12 - displayPrice * 12) : 0;
  const period = isNativeYearly ? "yr" : "mo";

  return (
    <div
      data-testid={`pricing-card-${plan.id}`}
      className={`pricing-card relative flex flex-col p-6 ${plan.highlighted ? "highlighted card-glow-animate" : ""}`}
      style={{
        background: plan.highlighted
          ? "linear-gradient(135deg, rgba(0,243,255,0.06) 0%, rgba(10,10,18,0.95) 40%)"
          : "rgba(10, 10, 18, 0.85)",
      }}
    >
      {/* Badge */}
      {plan.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="plan-badge flex items-center gap-1">
            {plan.badge === "MOST POPULAR" && <Star size={9} fill="currentColor" />}
            {plan.badge}
          </span>
        </div>
      )}

      {/* Discount pill */}
      {showYearly ? (
        <div className="absolute top-4 right-4">
          <span className="text-xs font-rajdhani font-700 px-2 py-1 rounded-md"
            style={{ background: "rgba(0,255,148,0.12)", color: "#00FF94", border: "1px solid rgba(0,255,148,0.25)" }}>
            SAVE 20%
          </span>
        </div>
      ) : plan.discount_percent ? (
        <div className="absolute top-4 right-4">
          <span className="text-xs font-rajdhani font-700 px-2 py-1 rounded-md"
            style={{ background: "rgba(0,255,148,0.12)", color: "#00FF94", border: "1px solid rgba(0,255,148,0.25)" }}>
            -{plan.discount_percent}% OFF
          </span>
        </div>
      ) : null}

      {/* Name */}
      <div className="mb-4 mt-2">
        <h3 className="font-rajdhani font-700 text-lg text-white tracking-wide">{plan.name}</h3>
        {showYearly ? (
          <p className="text-xs text-gray-500 font-outfit mt-0.5 line-through">₹{plan.price.toLocaleString("en-IN")}/mo</p>
        ) : plan.original_price ? (
          <p className="text-xs text-gray-600 line-through font-outfit mt-0.5">
            ₹{plan.original_price.toLocaleString("en-IN")}/{isNativeYearly ? "yr" : "mo"}
          </p>
        ) : null}
      </div>

      {/* Price */}
      <div className="mb-2">
        <div className="flex items-end gap-1">
          <span
            className="font-orbitron font-900"
            style={{
              fontSize: "2.5rem", lineHeight: 1,
              background: plan.highlighted
                ? "linear-gradient(to right, #00F3FF, #BC13FE)"
                : "linear-gradient(to right, #fff, #a0a0b0)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}
          >
            ₹{displayPrice.toLocaleString("en-IN")}
          </span>
          <span className="text-gray-500 text-sm font-outfit mb-1">/{period}</span>
        </div>
        {showYearly && (
          <p className="text-xs font-outfit mt-1" style={{ color: "#00FF94" }}>
            Save ₹{annualSaving.toLocaleString("en-IN")}/yr — billed annually
          </p>
        )}
      </div>

      <div className="mb-4" />

      {/* Specs strip */}
      {plan.specs && Object.keys(plan.specs).length > 0 && (
        <div
          className="grid grid-cols-2 gap-2 mb-5 p-3 rounded-lg"
          style={{ background: "rgba(0,243,255,0.04)", border: "1px solid rgba(0,243,255,0.08)" }}
        >
          {Object.entries(plan.specs).slice(0, 4).map(([k, v]) => (
            <div key={k} className="text-center">
              <div className="font-rajdhani font-700 text-sm text-white">{v}</div>
              <div className="font-outfit text-xs text-gray-600 capitalize">{k.replace("_", " ")}</div>
            </div>
          ))}
        </div>
      )}

      {/* Features */}
      <div className="flex flex-col gap-2.5 mb-6 flex-1">
        {plan.features.slice(0, 7).map((feat, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <Check size={13} className="feature-check flex-shrink-0" />
            <span className="font-outfit text-sm text-gray-300" style={{ fontWeight: 300 }}>{feat}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      <button
        data-testid={`plan-select-${plan.id}`}
        onClick={() => onSelect && onSelect(plan)}
        className="w-full py-3 rounded-lg font-rajdhani font-700 text-sm tracking-wider transition-all duration-300 flex items-center justify-center gap-2"
        style={
          plan.highlighted
            ? { background: "linear-gradient(135deg, #00F3FF, #BC13FE)", color: "#030305", boxShadow: "0 0 20px rgba(0, 243, 255, 0.3)" }
            : { background: "rgba(0, 243, 255, 0.08)", border: "1px solid rgba(0, 243, 255, 0.25)", color: "#00F3FF" }
        }
      >
        {plan.highlighted && <Zap size={14} />}
        GET STARTED
      </button>
    </div>
  );
}
